import { useCallback, useEffect, useMemo, useState } from "react";
import {
  clearAccessToken,
  getAccessToken,
  persistLoginState,
  restoreLoginState,
  storeAccessToken,
} from "../taskpane";
import { fetchRoles, loginRequest, fetchProfile, fetchDatabaseGrants } from "../api/authApi";

export const AUTH_STATUS = {
  CHECKING: "checking",
  ANONYMOUS: "anonymous",
  AUTHENTICATED: "authenticated",
};

const EMPTY_GRANTS = { catalogIds: [], resourceKinds: [] };

export function useAuthManager(officeReady) {
  const [status, setStatus] = useState(AUTH_STATUS.CHECKING);
  const [token, setToken] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSuperuser, setIsSuperuser] = useState(false);
  const [roles, setRoles] = useState([]);
  const [databaseGrants, setDatabaseGrants] = useState(EMPTY_GRANTS);
  const [authError, setAuthError] = useState("");
  const [loginBusy, setLoginBusy] = useState(false);

  const resetState = useCallback(() => {
    setToken(null);
    setIsAdmin(false);
    setIsSuperuser(false);
    setRoles([]);
    setDatabaseGrants(EMPTY_GRANTS);
    setAuthError("");
  }, []);

  const logout = useCallback(async () => {
    await persistLoginState(false);
    await clearAccessToken();
    resetState();
    setStatus(AUTH_STATUS.ANONYMOUS);
  }, [resetState]);

  const hydrateSession = useCallback(
    async (authToken) => {
      try {
        const [roleList, profile, grants] = await Promise.all([
          fetchRoles(authToken),
          fetchProfile(authToken).catch(() => null),
          fetchDatabaseGrants(authToken).catch(() => null),
        ]);
        const safeRoles = Array.isArray(roleList) ? roleList : [];
        setToken(authToken);
        setRoles(safeRoles);
        setIsAdmin(safeRoles.some((role) => role.role === "admin"));
        setIsSuperuser(Boolean(profile?.is_superuser));
        setDatabaseGrants({
          catalogIds: grants?.catalog_ids || [],
          resourceKinds: grants?.resource_kinds || [],
        });
        setStatus(AUTH_STATUS.AUTHENTICATED);
        setAuthError("");
        return true;
      } catch (error) {
        if (error?.status === 401) {
          await logout();
          return false;
        }
        setAuthError(error.message || "Unable to load account data.");
        return false;
      }
    },
    [logout]
  );

  const login = useCallback(
    async ({ email, password }) => {
      setLoginBusy(true);
      setAuthError("");

      try {
        const loginData = await loginRequest(email, password);
        const accessToken = loginData?.access_token;

        if (!accessToken) {
          throw new Error("Access token missing in response.");
        }

        await storeAccessToken(accessToken);
        await persistLoginState(true);

        const ok = await hydrateSession(accessToken);
        if (!ok) {
          throw new Error("Unable to load account data after sign-in.");
        }
      } catch (error) {
        await logout();
        setAuthError(error.message || "Sign-in failed. Check your credentials and try again.");
        throw error;
      } finally {
        setLoginBusy(false);
      }
    },
    [hydrateSession, logout]
  );

  const handleUnauthorized = useCallback(async () => {
    await logout();
  }, [logout]);

  const clearAuthError = useCallback(() => setAuthError(""), []);

  const refreshRoles = useCallback(async () => {
    if (!token) return;
    try {
      const roleList = await fetchRoles(token);
      const safeRoles = Array.isArray(roleList) ? roleList : [];
      setRoles(safeRoles);
      setIsAdmin(safeRoles.some((role) => role.role === "admin"));
    } catch (error) {
      if (error?.status === 401) {
        await logout();
      }
    }
  }, [token, logout]);

  useEffect(() => {
    if (!officeReady) return;

    let cancelled = false;

    (async () => {
      setStatus(AUTH_STATUS.CHECKING);
      const remembered = await restoreLoginState();
      if (!remembered) {
        if (!cancelled) setStatus(AUTH_STATUS.ANONYMOUS);
        return;
      }

      const savedToken = await getAccessToken();
      if (!savedToken) {
        if (!cancelled) setStatus(AUTH_STATUS.ANONYMOUS);
        return;
      }

      const ok = await hydrateSession(savedToken);
      if (!ok && !cancelled) {
        setStatus(AUTH_STATUS.ANONYMOUS);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [officeReady, hydrateSession]);

  const getProjectRole = useCallback(
    (projectId) => {
      if (!projectId) return null;
      const match = roles.find(
        (r) => String(r.project_id) === String(projectId)
      );
      return match ? match.role : null;
    },
    [roles]
  );

  const isAdminOfProject = useCallback(
    (projectId) => {
      if (isSuperuser) return true;
      return getProjectRole(projectId) === "admin";
    },
    [isSuperuser, getProjectRole]
  );

  const isRedactorOfProject = useCallback(
    (projectId) => {
      if (isSuperuser) return true;
      const role = getProjectRole(projectId);
      return role === "admin" || role === "redactor";
    },
    [isSuperuser, getProjectRole]
  );

  return useMemo(
    () => ({
      status,
      token,
      isAdmin,
      isSuperuser,
      roles,
      databaseGrants,
      getProjectRole,
      isAdminOfProject,
      isRedactorOfProject,
      refreshRoles,
      authError,
      loginBusy,
      login,
      logout,
      clearAuthError,
      handleUnauthorized,
    }),
    [
      status,
      token,
      isAdmin,
      isSuperuser,
      roles,
      databaseGrants,
      getProjectRole,
      isAdminOfProject,
      isRedactorOfProject,
      refreshRoles,
      authError,
      loginBusy,
      login,
      logout,
      clearAuthError,
      handleUnauthorized,
    ]
  );
}
